import { AutomationStrategy } from "@redotech/redo-model/team";
import { Divider } from "@redotech/redo-web/divider";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { FormSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { PackageProtectionForm } from "@redotech/redo-web/settings-elements/package-protection-elements";
import { FormSwitch } from "@redotech/redo-web/switch";
import { FormTextInput } from "@redotech/redo-web/text-input";
import classNames from "classnames";
import { memo } from "react";

const automationStrategyToTitle: Record<AutomationStrategy, string> = {
  [AutomationStrategy.TIME_BASED_APPROVAL]: "Time-based approval",
  [AutomationStrategy.AUTO_REJECT_APPROVE]: "Auto reject/approve",
  [AutomationStrategy.MERCHANT_MR]: "Merchant manual review",
  [AutomationStrategy.APPROVE]: "Approve immediately",
};

const automationStrategyDescriptions: Record<AutomationStrategy, string> = {
  [AutomationStrategy.TIME_BASED_APPROVAL]:
    "Claims are automatically approved as Redo-owned claims after a certain amount of time has passed.",
  [AutomationStrategy.AUTO_REJECT_APPROVE]:
    "Claims are automatically rejected as Redo-owned claims after the claim has been resolved by our insurance provider.",
  [AutomationStrategy.MERCHANT_MR]:
    "If the insurance provider rejects the claim, the merchant will go through a manual review process to approve or reject it as a merchant-owned claim.",
  [AutomationStrategy.APPROVE]:
    "Claims are automatically approved. Should only be used if All Claims Covered is enabled.",
};

export const PackageProtectionGeneralCard = memo(
  function PackageProtectionGeneralCard({
    input,
  }: {
    input: PackageProtectionForm;
  }) {
    const {
      enabled,
      customerClaimsEnabled,
      packageProtectionPlusEnabled,
      packageProtectionPlusPercentage,
      packageProtectionPlusAllOrdersCovered,
      packageProtectionPlusAllClaimsCovered,
      packageProtectionPlusShippingIncluded,
      newClaimsTables,
    } = input.inputs;

    return (
      <section className={gridCss.grid}>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch input={enabled} label="Enabled">
            Enable Package Protection
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch
            input={customerClaimsEnabled}
            label="Customer claim processing"
          >
            Enable customers to create claims in Customer Portal
          </FormSwitch>
        </div>

        <div className={classNames(gridCss.span12)}>
          <Divider />
        </div>

        <div className={gridCss.span12}>
          <strong>Package Protection Plus</strong>
        </div>

        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch input={packageProtectionPlusEnabled} label="Enabled">
            Enable Package Protection plus
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormTextInput
            description="Percentage of package protection coverage that Redo keeps."
            input={packageProtectionPlusPercentage}
            label="Percentage"
            max={100}
            min={0.01}
            step={0.01}
            suffix="%"
          />
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch
            input={packageProtectionPlusAllOrdersCovered}
            label="All Orders Covered"
          >
            If enabled, all orders will be marked package protected whether or
            not they bought coverage.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch
            input={packageProtectionPlusAllClaimsCovered}
            label="All Claims Covered"
          >
            If enabled, all claims will be covered by Package Protection Plus,
            even if the claim is not reported to Shipsurance. The customer will
            still need to buy coverage.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch
            input={packageProtectionPlusShippingIncluded}
            label="Shipping Included"
          >
            If enabled, the shipping cost will be included in the protection
            coverage when calculating the package protection amount.
          </FormSwitch>
        </div>
        <div className={classNames(gridCss.span12, gridCss.span6L)}>
          <FormSwitch input={newClaimsTables} label="New Claims Tables">
            Enable new UI tables for claims management
          </FormSwitch>
        </div>
        <AutomationStrategyOptions input={input} />
      </section>
    );
  },
);

const AutomationStrategyOptions = memo(function AutomationStrategyOptions({
  input,
}: {
  input: PackageProtectionForm;
}) {
  const { automationStrategy, timeTillApprovalHours } = input.inputs;
  return (
    <>
      <div className={classNames(gridCss.span12)}>
        <FormSelectDropdown
          description={automationStrategyDescriptions[automationStrategy.value]}
          input={automationStrategy}
          label="Automation strategy"
          options={Object.values(AutomationStrategy)}
        >
          {(option) => automationStrategyToTitle[option]}
        </FormSelectDropdown>
      </div>
      {automationStrategy.value === AutomationStrategy.TIME_BASED_APPROVAL && (
        <div className={classNames(gridCss.span12)}>
          <FormTextInput
            description="Time until the claim is automatically approved."
            input={timeTillApprovalHours}
            label="Hours until approval"
            min={1}
            step={1}
            type="number"
          />
        </div>
      )}
    </>
  );
});
